import { formatDistanceKm, formatKg } from '@common/utils/formaters';
import cn from 'classnames';
import { observer } from 'mobx-react';
import React, { FC, useMemo } from 'react';

import { COLORS, makeStyles } from '~/@components/@theme';
import { Grid } from '~/@components/Grid';
import { LocalizableText } from '~/@components/LocalizableText';
import {
    dashboardFilterStore,
    ProjectDeliveryItem,
    ProjectTimeSpentItem,
} from '~/@views/UserView/ProjectPage/ProjectDashboard/dashboardFilter.store';
import { commonPrecisions } from '~/config/enum';
import i18n from '~/i18n';
import { fmtDecimal } from '~/utils/numbers';

type TableRow = {
    licensePlate: string;
    vehicleTypeName: string;
    fuel: string;
    engine: string;
    distance: number;
    hours: number;
    CO2Consumption: number;
};

const getKey = (i: ProjectDeliveryItem | ProjectTimeSpentItem) =>
    `${i.licensePlate}_${i.vehicleType?.name || i18n.NA}_${i.engine}_${i.fuel}`;

export const ProjectCO2DriversTableDashboard: FC = observer(() => {
    const { table, tableHeader, textCell, valueCell, even, boldCell } = useStyles();

    const { series, totalKm, totalHours, totalCO2 } = useMemo(() => {
        const { map, totalKm, totalCO2 } = dashboardFilterStore.filteredDeliveryItems.reduce(
            (acc, cur) => {
                const key = getKey(cur);
                if (!acc.map.has(key))
                    acc.map.set(key, {
                        licensePlate: cur.licensePlate,
                        vehicleTypeName: cur.vehicleType?.name || i18n.NA,
                        fuel: cur.fuel,
                        engine: cur.engine,
                        distance: 0,
                        hours: 0,
                        CO2Consumption: 0,
                    });

                const row = acc.map.get(key)!;
                row.CO2Consumption += cur.CO2Consumption;
                row.distance += cur.distance;

                acc.totalCO2 += cur.CO2Consumption;
                acc.totalKm += cur.distance;

                return acc;
            },
            { map: new Map<string, TableRow>(), totalKm: 0, totalCO2: 0 }
        );

        const res = dashboardFilterStore.filteredTimeSpentItems
            .filter(i => !!i.driver)
            .reduce(
                (acc, cur) => {
                    const key = getKey(cur);
                    if (!acc.map.has(key))
                        acc.map.set(key, {
                            licensePlate: cur.licensePlate,
                            vehicleTypeName: cur.vehicleType?.name || i18n.NA,
                            fuel: cur.fuel,
                            engine: cur.engine,
                            distance: 0,
                            hours: 0,
                            CO2Consumption: 0,
                        });

                    const row = acc.map.get(key)!;
                    row.CO2Consumption += cur.CO2Consumption;
                    row.hours += cur.hours;

                    acc.totalCO2 += cur.CO2Consumption;
                    acc.totalHours += cur.hours;

                    return acc;
                },
                { map, totalHours: 0, totalCO2 }
            );

        const keys = Array.from(res.map.keys()).sort();

        const series = keys.map(k => res.map.get(k)!);

        return { series, totalKm, totalHours: res.totalHours, totalCO2: res.totalCO2 };
    }, [dashboardFilterStore.filteredDeliveryItems, dashboardFilterStore.filteredTimeSpentItems]);

    return (
        <div className={table}>
            <Grid container spacing={1}>
                <Grid item xs={12} className={tableHeader}>
                    <LocalizableText code={'ProjectDashboard.CO2Table.drivers'} />
                </Grid>
                <Grid item xs={2} className={cn(textCell, boldCell)}>
                    <LocalizableText code={'ProjectDashboard.CO2Table.regNumber'} />
                </Grid>
                <Grid item xs={2} className={cn(textCell, boldCell)}>
                    <LocalizableText code={'ProjectDashboard.CO2Table.vehicleType'} />
                </Grid>
                <Grid item xs={1} className={cn(textCell, boldCell)}>
                    <LocalizableText code={'ProjectDashboard.CO2Table.fuel'} />
                </Grid>
                <Grid item xs={1} className={cn(textCell, boldCell)}>
                    <LocalizableText code={'ProjectDashboard.CO2Table.engine'} />
                </Grid>
                <Grid item xs={2} className={cn(textCell, boldCell)}>
                    <LocalizableText code={'ProjectDashboard.CO2Table.distance'} />
                </Grid>
                <Grid item xs={2} className={cn(textCell, boldCell)}>
                    <LocalizableText code={'ProjectDashboard.CO2Table.hours'} />
                </Grid>
                <Grid item xs={2} className={cn(textCell, boldCell)}>
                    <LocalizableText code={'ProjectDashboard.CO2Table.CO2'} />
                </Grid>
                {series.map((row, i) => (
                    <React.Fragment key={i}>
                        <Grid item xs={2} className={i % 2 === 0 ? even : undefined}>
                            {row.licensePlate}
                        </Grid>
                        <Grid item xs={2} className={cn(textCell, i % 2 === 0 ? even : undefined)}>
                            {row.vehicleTypeName}
                        </Grid>
                        <Grid item xs={1} className={cn(textCell, i % 2 === 0 ? even : undefined)}>
                            {row.fuel}
                        </Grid>
                        <Grid item xs={1} className={cn(textCell, i % 2 === 0 ? even : undefined)}>
                            {row.engine}
                        </Grid>
                        <Grid item xs={2} className={cn(valueCell, i % 2 === 0 ? even : undefined)}>
                            {row.distance === 0 ? null : formatDistanceKm(row.distance, false)}
                        </Grid>
                        <Grid item xs={2} className={cn(valueCell, i % 2 === 0 ? even : undefined)}>
                            {row.hours === 0 ? null : fmtDecimal(row.hours, commonPrecisions.amount)}
                        </Grid>
                        <Grid item xs={2} className={cn(valueCell, i % 2 === 0 ? even : undefined)}>
                            {formatKg(row.CO2Consumption, false)}
                        </Grid>
                    </React.Fragment>
                ))}
                <Grid item xs={6} className={cn(valueCell, boldCell)}>
                    <LocalizableText code={'ProjectDashboard.CO2Table.total'} />
                </Grid>
                <Grid item xs={2} className={cn(valueCell, boldCell)}>
                    {formatDistanceKm(totalKm, false)}
                </Grid>
                <Grid item xs={2} className={cn(valueCell, boldCell)}>
                    {fmtDecimal(totalHours, commonPrecisions.amount)}
                </Grid>
                <Grid item xs={2} className={cn(valueCell, boldCell)}>
                    {formatKg(totalCO2, false)}
                </Grid>
            </Grid>
        </div>
    );
});

const useStyles = makeStyles(theme => ({
    table: {
        height: 'auto',
        fontFamily: 'Catamaran',
        gap: '12px',
        alignItems: 'center',
        fontSize: '16px',

        [theme.breakpoints.down('md')]: {
            fontSize: '14px',
        },
    },
    tableHeader: {
        textAlign: 'center',
        fontSize: '18px',

        [theme.breakpoints.down('md')]: {
            fontSize: '16px',
        },
    },
    textCell: {
        textAlign: 'center',
    },
    valueCell: {
        textAlign: 'right',
    },
    boldCell: {
        fontWeight: 'bold',
    },
    even: {
        background: COLORS.grayLight,
    },
}));
